import axios from 'axios';
import config from '../config';
import { AddressReportResponse } from './addressManagementService';

const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent';

const GeminiService = {
    summarizeAddressReport: async (data: AddressReportResponse): Promise<string> => {
        const apiKey = config.GEMINI_API_KEY;

        if (!apiKey) {
            throw new Error('Gemini API key is not configured.');
        }

        // Prepare the data for the prompt
        // We'll simplify the data payload to focus on the key validation aspects to save tokens and reduce noise
        const simplifiedData = {
            address: {
                street1: data.address.street1,
                city: data.address.city,
                state: data.address.stateCode,
                zip: data.address.zip,
                isValid: data.address.isValid,
                validationSource: data.address.validationSource,
                // Provider 1 (Google)
                provider1Rating: data.address.googleScore,
                provider1Valid: data.address.isGoogleValid,
                provider1NextAction: data.address.googlePossibleNextAction,
                // Provider 2 (Radar)
                provider2Rating: data.address.radarScore,
                provider2Valid: data.address.isRadarValid,
                geoFenceConfidence: data.address.geoFenceConfidence
            },
            // We can verify if we need to parse the JSON strings in addressValidationInfo or if just the high level is enough.
            // For now, let's just send the high level address object which has the consolidated data.
        };

        const isAccept = simplifiedData.address.provider1NextAction === 'ACCEPT';

        const prompt = `
            You are an expert logistics analyst providing an executive summary.
            Analyze the following address validation data from "BetterTrucks Checkpoint".
            
            CRITICAL RULE: NEVER reveal the real names of the providers. Refer to them ONLY as "Provider 1" and "Provider 2".

            CONTEXT ON SCORES:
            - "Provider 1" score range is 0-255. 255 is the BEST possible score.
            - "Provider 2" score range is 0-100. 100 is the BEST possible score.
            - Do NOT treat 255 as "higher" than 100 in a way that implies discrepancy. They are both maximum scores.
            
            OUTPUT FORMAT:
            Return a valid JSON object with the following keys. Do not include markdown formatting like \`\`\`json.
            {
                "deliverable": "${isAccept ? "Start with 'Yes', 'No', or 'Risk', then a brief explanation." : "Start with 'Yes', 'No', or 'Risk', then provide a detailed explanation citing specific data points (e.g., specific scores, confidence levels) to support your conclusion."}",
                "discrepancy": "${isAccept ? "Briefly check for any major discrepancies." : "Provide a comprehensive analysis of any differences between providers. If scores differ significantly (normalizing for their scales), explain the potential impact. If they align, explicitly state how they reinforce each other."}",
                "recommendation": "${isAccept ? "Brief final recommendation." : "Final detailed recommendation for the executive, including any specific actions if the address is risky."}"
            }

            Address Data:
            ${JSON.stringify(simplifiedData, null, 2)}
        `;

        try {
            const response = await axios.post(
                GEMINI_API_URL,
                {
                    contents: [{
                        parts: [{ text: prompt }]
                    }]
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-goog-api-key': apiKey
                    }
                }
            );

            if (response.data && response.data.candidates && response.data.candidates.length > 0) {
                const text = response.data.candidates[0].content.parts[0].text;
                // Clean any potential markdown fencing if the model ignores the instruction
                const cleanText = text.replace(/```json/g, '').replace(/```/g, '').trim();
                return cleanText;
            } else {
                throw new Error('No content generated from Gemini API.');
            }

        } catch (error) {
            console.error('Error calling Gemini API:', error);
            throw error;
        }
    }
};

export default GeminiService;
